import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import type { ComboboxCommonProps, ComboboxItem } from '@/components'
import { Combobox } from '@/components'
import { VEHICLE_PLATE_NUMBERS_QUERY } from '@/modules/vehicles'
import { useOnErrorAuto } from '@/utils/hooks'
import type {
  VehiclePlateNumbersQuery,
  VehiclePlateNumbersQueryVariables,
} from '../types.graphl'

interface VehicleSelectProps extends ComboboxCommonProps<string> {
  value: string | null
  clearable?: boolean
  onChange(value: string): void
}

/**
 * Picks a vehicle by registration number — issues store the plate number
 * itself rather than a vehicle id. The backend only returns plate numbers
 * for currently owned vehicles.
 */
export const VehicleSelect: React.FC<VehicleSelectProps> = ({
  value,
  clearable = true,
  placeholder,
  searchPlaceholder,
  noResultsText,
  onChange,

  ...comboboxProps
}) => {
  const onErrorAuto = useOnErrorAuto()

  const translations = useTranslate({
    placeholder: 'components.combobox.vehicle.placeholder',
    searchPlaceholder: 'components.combobox.vehicle.search-placeholder',
    noResults: 'components.combobox.vehicle.no-results',
    loading: 'components.combobox.vehicle.loading',
  })

  const { data, loading } = useQuery<
    VehiclePlateNumbersQuery,
    VehiclePlateNumbersQueryVariables
  >(VEHICLE_PLATE_NUMBERS_QUERY, {
    onError: onErrorAuto(),
  })

  const items = useMemo<ComboboxItem<string>[]>(
    () =>
      (data?.vehiclePlateNumbers ?? []).map(plateNumber => ({
        value: plateNumber,
        label: plateNumber,
      })),
    [data?.vehiclePlateNumbers]
  )

  return (
    <Combobox
      {...comboboxProps}
      mode="single"
      value={value ?? ''}
      items={items}
      clearable={clearable}
      placeholder={placeholder ?? translations.placeholder}
      searchPlaceholder={searchPlaceholder ?? translations.searchPlaceholder}
      noResultsText={
        loading ? translations.loading : noResultsText ?? translations.noResults
      }
      onChange={onChange}
    />
  )
}
